import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  InstagramLogo, LinkedinLogo, FacebookLogo, TiktokLogo, YoutubeLogo, TwitterLogo,
  CheckCircle, WarningCircle, ArrowsClockwise, Plug, PlugsConnected, Trash, Shield, Info
} from '@phosphor-icons/react';
import DashboardLayout from '../components/DashboardLayout';

const API = process.env.REACT_APP_BACKEND_URL;

const PLATFORM_ICONS = {
  instagram: InstagramLogo, facebook: FacebookLogo, linkedin: LinkedinLogo,
  tiktok: TiktokLogo, x: TwitterLogo, youtube: YoutubeLogo,
};
const PLATFORM_COLORS = {
  instagram: '#E4405F', facebook: '#1877F2', linkedin: '#0A66C2',
  tiktok: '#000000', x: '#1DA1F2', youtube: '#FF0000',
};

export default function SocialAccountsPage() {
  const [platforms, setPlatforms] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);
  const [disconnecting, setDisconnecting] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pRes, aRes] = await Promise.all([
        axios.get(`${API}/api/social/platforms`),
        axios.get(`${API}/api/social/accounts`),
      ]);
      setPlatforms(pRes.data.platforms || []);
      setAccounts(aRes.data.accounts || []);
    } catch {}
    setLoading(false);
  };

  const connect = async (platformId) => {
    setConnecting(platformId);
    try {
      await axios.post(`${API}/api/social/connect`, { platform: platformId });
      await fetchData();
    } catch {}
    setConnecting(null);
  };

  const disconnect = async (accountId) => {
    setDisconnecting(accountId);
    try {
      await axios.delete(`${API}/api/social/disconnect/${accountId}`);
      await fetchData();
    } catch {}
    setDisconnecting(null);
  };

  const getAccount = (platformId) => accounts.find(a => a.platform === platformId);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="social-accounts-page">
        <div>
          <h1 className="text-2xl font-semibold" style={{ color: '#f5f0e8', fontFamily: "'Instrument Serif', serif" }}>
            Social Accounts
          </h1>
          <p className="text-sm mt-1" style={{ color: 'rgba(245,240,232,0.4)' }}>
            Connect your accounts to publish content directly from ScalitOS.
          </p>
        </div>

        {/* Demo mode banner */}
        <div className="flex items-start gap-3 px-4 py-3 rounded-xl" data-testid="demo-mode-banner"
          style={{ background: 'rgba(200,240,96,0.04)', border: '1px solid rgba(200,240,96,0.1)' }}>
          <Info size={18} className="flex-shrink-0 mt-0.5" style={{ color: '#c8f060' }} />
          <div>
            <p className="text-xs font-semibold" style={{ color: '#c8f060' }}>Demo Mode</p>
            <p className="text-xs mt-0.5" style={{ color: 'rgba(245,240,232,0.4)' }}>
              Your publishing workflow is ready — direct posting will be activated soon.
              Connections here simulate the full flow so you can plan, draft, schedule, and preview posts.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1,2,3].map(i => <div key={i} className="h-44 rounded-xl animate-pulse" style={{ background: '#141414' }} />)}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {platforms.map(platform => {
              const account = getAccount(platform.id);
              const Icon = PLATFORM_ICONS[platform.id] || Plug;
              const color = PLATFORM_COLORS[platform.id] || '#888';
              const isEnabled = platform.enabled;
              const isConnected = !!account;

              return (
                <motion.div
                  key={platform.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-xl overflow-hidden"
                  style={{ background: '#141414', border: `1px solid ${isConnected ? `${color}33` : 'rgba(255,255,255,0.06)'}` }}
                  data-testid={`platform-card-${platform.id}`}
                >
                  <div className="p-5">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${color}15` }}>
                          <Icon size={22} weight="fill" style={{ color }} />
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold" style={{ color: '#f5f0e8' }}>{platform.name}</h3>
                          <p className="text-[10px]" style={{ color: 'rgba(245,240,232,0.3)' }}>
                            {isEnabled ? (isConnected ? 'Connected' : 'Available') : `Coming soon (Phase ${platform.phase})`}
                          </p>
                        </div>
                      </div>
                      {isConnected && (
                        <CheckCircle size={20} weight="fill" className="text-green-400" />
                      )}
                    </div>

                    {isConnected ? (
                      <div className="space-y-3">
                        <div className="px-3 py-2.5 rounded-lg" style={{ background: '#0a0a0a' }}>
                          <p className="text-xs font-medium" style={{ color: '#f5f0e8' }}>{account.display_name}</p>
                          <p className="text-[10px] mt-0.5" style={{ color: 'rgba(245,240,232,0.3)' }}>
                            {account.account_type} account
                            {account.is_demo && ' · demo mode'}
                          </p>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5">
                            <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
                            <span className="text-[10px]" style={{ color: 'rgba(245,240,232,0.3)' }}>Healthy</span>
                          </div>
                          <button
                            onClick={() => disconnect(account.account_id)}
                            disabled={disconnecting === account.account_id}
                            data-testid={`disconnect-${platform.id}`}
                            className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-medium transition-all hover:bg-red-500/20"
                            style={{ color: 'rgba(239,68,68,0.6)', border: '1px solid rgba(239,68,68,0.15)' }}
                          >
                            {disconnecting === account.account_id ? <ArrowsClockwise size={10} className="animate-spin" /> : <Trash size={10} />}
                            Disconnect
                          </button>
                        </div>
                      </div>
                    ) : isEnabled ? (
                      <button
                        onClick={() => connect(platform.id)}
                        disabled={connecting === platform.id}
                        data-testid={`connect-${platform.id}`}
                        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-semibold transition-all hover:opacity-90"
                        style={{ background: `${color}20`, color, border: `1px solid ${color}30`, opacity: connecting === platform.id ? 0.5 : 1 }}
                      >
                        {connecting === platform.id ? <ArrowsClockwise size={14} className="animate-spin" /> : <PlugsConnected size={14} />}
                        {connecting === platform.id ? 'Connecting...' : `Connect ${platform.name}`}
                      </button>
                    ) : (
                      <div className="py-3 text-center rounded-lg" style={{ background: 'rgba(255,255,255,0.02)', border: '1px dashed rgba(255,255,255,0.06)' }}>
                        <p className="text-[11px] font-medium" style={{ color: 'rgba(245,240,232,0.2)' }}>Coming Soon</p>
                        <p className="text-[10px]" style={{ color: 'rgba(245,240,232,0.1)' }}>Phase {platform.phase} platform</p>
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Security note */}
        <div className="flex items-center gap-2 px-4 py-3 rounded-xl" style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.04)' }}>
          <Shield size={16} style={{ color: 'rgba(245,240,232,0.2)' }} />
          <p className="text-[11px]" style={{ color: 'rgba(245,240,232,0.2)' }}>
            All access tokens are encrypted at rest. ScalitOS never stores plain-text credentials.
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
}
